"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { CookiePreferencesModal } from "@/components/CookiePreferencesModal";
import {
  CookieConsent,
  DEFAULT_COOKIE_CONSENT,
  getCookieConsent,
  hasStoredCookieConsent,
  setCookieConsent,
} from "@/lib/consent";
import { updateConsentFromState } from "@/lib/analytics/consent";
import { GlassPanel } from "@/components/ui/GlassPanel";

const COOKIE_PREFERENCES_EVENT = "sa:cookie-preferences-open";
const GA_CONSENT_STORAGE_KEY = "sa-cookie-consent";
const CONSENT_VERSION = "2026-03-01";

export function CookieBanner() {
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_COOKIE_CONSENT);

  useEffect(() => {
    if (!mounted) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydration sync from browser storage.
    setIsBannerVisible(!hasStoredCookieConsent());
    setConsent(getCookieConsent());
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const handler = () => setIsPreferencesOpen(true);
    window.addEventListener(COOKIE_PREFERENCES_EVENT, handler);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, handler);
  }, [mounted]);

  const persistConsent = (nextConsent: CookieConsent) => {
    const effective = setCookieConsent(nextConsent);
    setConsent(effective);
    try {
      window.localStorage.setItem(
        GA_CONSENT_STORAGE_KEY,
        JSON.stringify({
          analytics: effective.analytics ? "granted" : "denied",
          marketing: effective.marketing ? "granted" : "denied",
          version: CONSENT_VERSION,
          updatedAt: new Date().toISOString(),
        }),
      );
    } catch {
      // The canonical effective choice remains authoritative without metadata.
    }
    updateConsentFromState({
      analytics: effective.analytics ? "granted" : "denied",
      marketing: effective.marketing ? "granted" : "denied",
    }, { source: "decision" });
    setIsBannerVisible(false);
    setIsPreferencesOpen(false);
  };

  if (!mounted) return null;
  if (!isBannerVisible && !isPreferencesOpen) return null;

  return (
    <div
      data-cookie-banner
      role="region"
      aria-label="Cookie settings"
      className="pointer-events-none fixed bottom-1.5 left-1.5 right-1.5 z-50 flex flex-col items-end gap-3 sm:bottom-5 sm:left-auto sm:right-5 sm:w-[min(680px,calc(100vw-2.5rem))]"
    >
      {isPreferencesOpen && (
        <CookiePreferencesModal
          consent={consent}
          onChange={setConsent}
          onSave={() => persistConsent(consent)}
          onClose={() => setIsPreferencesOpen(false)}
        />
      )}

      {isBannerVisible && (
        <GlassPanel className="pointer-events-auto w-full rounded-[8px] border-border bg-surface/90 p-1.5 text-sm text-offwhite/80 shadow-[0_10px_22px_rgba(2,4,13,0.2)] sm:p-3">
          <div className="flex items-center gap-1.5 sm:gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald sm:text-xs sm:tracking-[0.3em]">
                <span className="sm:hidden">Cookies</span>
                <span className="hidden sm:inline">Cookie consent</span>
              </p>
              <h2 className="sr-only">Choose cookie settings</h2>
              <p className="mt-0.5 hidden truncate text-[11px] leading-relaxed text-offwhite/68 sm:block">
                Optional analytics and marketing tags only run with consent.
              </p>
            </div>
            <div className="flex shrink-0 flex-nowrap items-center gap-1.5 overflow-x-auto sm:gap-2 sm:overflow-visible">
              <button
                type="button"
                onClick={() =>
                  persistConsent({
                    necessary: true,
                    preferences: true,
                    analytics: true,
                    marketing: true,
                  })
                }
                className="inline-flex shrink-0 items-center justify-center rounded-[8px] bg-emerald/90 px-2.5 py-1 text-[10px] font-semibold text-black shadow-emerald/20 transition hover:-translate-y-px hover:bg-emerald hover:shadow-md sm:px-3.5 sm:py-2 sm:text-[11px]"
                style={{ color: "#05070b" }}
              >
                <span className="sm:hidden">Accept</span>
                <span className="hidden sm:inline">Accept all</span>
              </button>
              <button
                type="button"
                onClick={() => persistConsent(DEFAULT_COOKIE_CONSENT)}
                className="capsule-link shrink-0 rounded-[8px] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-offwhite/72 hover:text-offwhite sm:px-3 sm:py-1.5 sm:text-[11px] sm:tracking-[0.24em]"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => setIsPreferencesOpen(true)}
                className="capsule-link shrink-0 rounded-[8px] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-offwhite/72 hover:text-offwhite sm:px-3 sm:py-1.5 sm:text-[11px] sm:tracking-[0.24em]"
              >
                Manage
              </button>
            </div>
          </div>
        </GlassPanel>
      )}
    </div>
  );
}
